import React, { useState, useEffect, useMemo } from 'react'
import clsx from 'clsx'
import * as R from 'ramda'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import {
  Divider,
  makeStyles,
  RadioGroup,
  Typography,
  InputLabel,
  FormControl,
  FormControlLabel,
} from '@material-ui/core'
import Selector from 'app/components/selector/Selector'
import { withStyleSelector } from 'app/components/selector/withStyleSelector'

import Radios from 'app/componentV4/Radios'
import CheckBox from 'app/componentV4/CheckBox'
import LabelError from 'app/componentV4/Labels/LabelError'

import { AUTHOR, ADMIN } from 'app/main/settings/teamMembersV4/constants'

import { renderLabelRoleName, sortRoleByName } from 'commons/roleName'

const useStyles = makeStyles((theme) => ({
  root: {
    '& .MuiOutlinedInput-root': {
      height: '48px',
      '& fieldset': {
        color: theme.colors['grey-400'],
        padding: '6px 10px',
      },
      '&:hover fieldset': {
        borderColor: theme.colors['grey-100'],
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.colors['grey-100'],
      },
      '&.Mui-error .MuiOutlinedInput-notchedOutline': {
        borderColor: `${theme.colors['red-500']} !important`,
      },
      '&.Mui-error:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: `${theme.colors['red-500']} !important`,
      },
      '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: `${theme.colors['red-500']} !important`,
      },
    },
    '& .MuiSelect-outlined.MuiSelect-outlined': {
      color: theme.colors.primary + '!important',
    },
    '& .MuiSelect-select.Mui-disabled': {
      backgroundColor: theme.colors['neutrals-100'] + '!important',
    },
  },
  formControl: {
    top: '0',
    left: '15px',
    position: 'absolute',
    transform: 'translate(0, 18px) scale(1)',
    color: theme.colors['grey-200'],
  },
  paper: {
    '& .MuiMenu-paper': {
      borderRadius: '.8rem',
    },
  },
}))

function RolesSelector({
  id,
  options = [],
  teamMemberName,
  value,
  emptyLabelId,
  onChange,
  placeholder,
  error,
  helperText,
  defaultValue,
  ...rest
}) {
  const classes = useStyles()
  // Memoize rolesByType to avoid re-sorting and re-grouping on every render
  const rolesByType = useMemo(() => {
    return R.compose(R.groupBy(R.prop('type')), R.sort(sortRoleByName))(options)
  }, [options])

  // Compute the initial selected type based on value, useMemo to avoid recalculating every render
  const initialSelectedType = useMemo(() => {
    return R.compose(
      R.ifElse(
        R.isEmpty,
        R.always(AUTHOR),
        R.compose(
          R.prop('type'),
          R.find(R.__, options),
          R.propEq('id'),
          R.head,
        ),
      ),
      R.defaultTo([]),
    )(value)
  }, [options, value])

  const [selectedType, setSelectedType] = useState(initialSelectedType)
  const [roleIds, setRoleIds] = useState([])

  const handleRoleChange = (roleId, isChecked) => {
    setRoleIds((prevRoleIds) =>
      isChecked
        ? [...prevRoleIds, roleId]
        : prevRoleIds.filter((id) => id !== roleId),
    )
  }

  useEffect(() => {
    if (onChange && !R.equals(roleIds, value)) {
      onChange(roleIds)
    }
  }, [roleIds, onChange, value])

  useEffect(() => {
    if (!R.isEmpty(value) && !R.isNil(defaultValue)) {
      // setSelectedType(defaultValue[0]?.type)
      setRoleIds(value)
    }
  }, [defaultValue, value])

  return (
    <FormControl className="w-full">
      {R.isEmpty(roleIds) && (
        <InputLabel className={classes.formControl}>{placeholder}</InputLabel>
      )}
      <Selector
        {...rest}
        className={clsx(classes.root, classes.paper)}
        InputProps={{ classes }}
        value={R.defaultTo('', roleIds)}
        renderValue={(selected) => renderLabelRoleName(options, selected)}
        shouldShowErrorMsg={error}
      >
        <div className="flex flex-col">
          <Typography className="mt-8 mx-16">
            {teamMemberName ? (
              <FormattedMessage
                id="teamMembers.roleSelector.description.withName.label"
                values={{ teamMemberName }}
              />
            ) : (
              <FormattedMessage id="teamMembers.roleSelector.description.withoutName.label" />
            )}
          </Typography>
          <RadioGroup
            className="flex flex-row mx-16"
            aria-label="role type"
            value={String(selectedType)}
            onChange={(event) => {
              setRoleIds([])
              setSelectedType(Number(event.target.value))
            }}
          >
            <FormControlLabel
              value={String(ADMIN)}
              control={<Radios checked={selectedType === ADMIN} />}
              label={<FormattedMessage id={'roleForm.type.admin'} />}
            />
            <FormControlLabel
              value={String(AUTHOR)}
              control={<Radios checked={selectedType === AUTHOR} />}
              label={<FormattedMessage id={'roleForm.type.author'} />}
            />
          </RadioGroup>
          <Divider className="my-8" />
          {R.isEmpty(options) ? (
            <FormattedMessage id={emptyLabelId || 'error.form.notFound'} />
          ) : (
            rolesByType[selectedType]?.map((role) => (
              <FormControlLabel
                key={role.id}
                className="ltr:ml-1 rtl:mr-1"
                control={
                  <CheckBox
                    label={role.name}
                    disableRipple
                    onClick={(event) =>
                      handleRoleChange(role.id, event.target.checked)
                    }
                    checked={roleIds.includes(role.id)}
                    value={role.id}
                  />
                }
                label={role.name}
              />
            ))
          )}
        </div>
      </Selector>
      {error && <LabelError id={id} text={helperText} />}
    </FormControl>
  )
}

// Define propTypes for the component
RolesSelector.propTypes = {
  id: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  placeholder: PropTypes.string,
  emptyLabelId: PropTypes.string,
  teamMemberName: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.arrayOf(PropTypes.number),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.number.isRequired,
    }),
  ).isRequired,
  defaultValue: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.number.isRequired,
    }),
  ),
}

export default React.memo(withStyleSelector(RolesSelector))
